/**
 * Corrections that are actually saved.
 *
 * The review screen used to hold every edit in component state and PATCH the
 * lot only when "Send to tablet" was pressed. Everything else threw the work
 * away without a word — clicking another job in the inbox, a live session
 * taking the pane, a refresh — and because the state was keyed to the
 * component rather than the job, switching between two cached registration
 * cards carried one guest's typing onto the other guest's document and saved
 * it there.
 *
 * So: a correction is written as it is made. Each field debounces on its own
 * timer, a blur flushes immediately, and the job id is captured per write so a
 * flight in progress can never land on a document the receptionist has since
 * moved away from.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { CaptureJob, Extraction } from '@/lib/types'
import { useToast } from '@/components/ui/toast'

const SETTLE_MS = 600

export interface Corrections {
  value: (extraction: Extraction) => string
  setValue: (fieldKey: string, value: string) => void
  /** Send anything still pending now, and wait for it. */
  flush: () => Promise<void>
  setCheckbox: (groupKey: string, options: string[]) => void
  dirtyCount: number
  saving: boolean
  failed: string[]
}

export function useCorrections(job: CaptureJob): Corrections {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [edits, setEdits] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(0)
  const [failed, setFailed] = useState<string[]>([])

  // Belt and braces alongside the `key` on the component: if this ever renders
  // for a different job without remounting, nothing carries over.
  const jobId = job.id
  useEffect(() => {
    setEdits({})
    setFailed([])
  }, [jobId])

  const pending = useRef(new Map<string, string>())
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const inflight = useRef<Promise<void>>(Promise.resolve())

  const send = useCallback(
    async (keys: string[]) => {
      const corrections = keys
        .map((field_key) => ({ field_key, value: pending.current.get(field_key) }))
        .filter((c): c is { field_key: string; value: string } => c.value !== undefined)
      if (corrections.length === 0) return

      keys.forEach((key) => pending.current.delete(key))
      setSaving((n) => n + 1)
      try {
        const saved = await api.patch<CaptureJob>(
          `/capture/jobs/${jobId}/extractions`,
          { corrections },
          )
        queryClient.setQueryData(['capture', 'job', jobId], saved)
        void queryClient.invalidateQueries({ queryKey: ['capture', 'inbox'] })
        setFailed((current) => current.filter((key) => !keys.includes(key)))
        // The server has the value now, and it may have normalised it — drop
        // our copy so what is on screen is what was actually stored.
        setEdits((current) => {
          const next = { ...current }
          for (const { field_key, value } of corrections) {
            const stored = saved.extractions?.find((e) => e.field_key === field_key)
            if (stored && (stored.value ?? '') === value) delete next[field_key]
            else if (stored) next[field_key] = stored.value ?? ''
          }
          return next
        })
      } catch (error) {
        setFailed((current) => [...new Set([...current, ...keys])])
        toast.error('That correction was not saved', (error as Error).message)
      } finally {
        setSaving((n) => n - 1)
      }
    },
    [jobId, queryClient, toast],
  )

  const queue = useCallback(
    (fieldKey: string) => {
      clearTimeout(timers.current.get(fieldKey))
      timers.current.set(
        fieldKey,
        setTimeout(() => {
          timers.current.delete(fieldKey)
          inflight.current = inflight.current.then(() => send([fieldKey]))
        }, SETTLE_MS),
      )
    },
    [send],
  )

  const setValue = useCallback(
    (fieldKey: string, value: string) => {
      setEdits((current) => ({ ...current, [fieldKey]: value }))
      pending.current.set(fieldKey, value)
      queue(fieldKey)
    },
    [queue],
  )

  const flush = useCallback(async () => {
    timers.current.forEach((timer) => clearTimeout(timer))
    timers.current.clear()
    const keys = [...pending.current.keys()]
    if (keys.length) inflight.current = inflight.current.then(() => send(keys))
    await inflight.current
  }, [send])

  // Whatever is still in flight when the screen goes away must still land.
  // `send` closes over the job id, so a late write cannot hit the wrong job.
  useEffect(() => {
    const runningTimers = timers.current
    const stillPending = pending.current
    return () => {
      runningTimers.forEach((timer) => clearTimeout(timer))
      runningTimers.clear()
      const keys = [...stillPending.keys()]
      if (keys.length) void send(keys)
    }
  }, [send])

  const setCheckbox = useCallback(
    (groupKey: string, options: string[]) => {
      setSaving((n) => n + 1)
      inflight.current = inflight.current
        .then(async () => {
          const saved = await api.patch<CaptureJob>(`/capture/jobs/${jobId}/checkboxes`, {
            checkboxes: { [groupKey]: options },
          })
          queryClient.setQueryData(['capture', 'job', jobId], saved)
        })
        .catch((error: Error) => toast.error('That tick was not saved', error.message))
        .finally(() => setSaving((n) => n - 1))
    },
    [jobId, queryClient, toast],
  )

  const value = useCallback(
    (extraction: Extraction) => edits[extraction.field_key] ?? extraction.value ?? '',
    [edits],
  )

  return useMemo(
    () => ({
      value,
      setValue,
      flush,
      setCheckbox,
      dirtyCount: Object.keys(edits).length,
      saving: saving > 0,
      failed,
    }),
    [value, setValue, flush, setCheckbox, edits, saving, failed],
  )
}
